import {
  FeatureName,
  PickerMode,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { RenderMediaItem } from './renderMediaItem';
import { MediaGalleryProps } from './types';
import { useTypedMedia } from './useTypedMedia';

export const HorizontalMediaGallery = (props: MediaGalleryProps) => {
  const { handleAdd, handleUploadProgress, typedMedia, handleDelete } =
    useTypedMedia(props.media);
  const [contentContainerStyle, setContentContainerStyle] = useState({} as any);
  const MediaPicker = props.mediaPicker;
  const scrollRef = useRef<ScrollView>(null);
  const canAddMedia = props.canAddMedia ?? true;
  const pickerMode =
    props.pickerMode === 'picker' ? PickerMode.PICKER : PickerMode.CAMERA;
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  useEffect(() => {
    if (Platform.OS === 'web') {
      let width = (typedMedia.length - 1) * 20 + 80;
      if (width < 380) width = 380;
      setContentContainerStyle({
        width: width,
      });
    }
    if (scrollRef.current) {
      scrollRef.current.scrollToEnd({ animated: true });
    }
  }, [typedMedia?.length]);

  // TODO: Crop Circle Redesign | Remove conditional rendering when feature is fully launched
  if (!isCropCircleRedesignEnabled) {
    return (
      <View style={styles.container}>
        {canAddMedia && (
          <MediaPicker
            workspaceId={props.workspaceId}
            setMedia={(media) => handleAdd(media, props.onMediaAdded)}
            mode={pickerMode}
            icon={'plus'}
            iconColor={Colors.primaryDark}
            iconSize={24}
            setProgress={handleUploadProgress}
            setUploading={props.onUploadingChanged}
            style={{
              ...styles.picker,
              marginTop: 10
            }}
            acceptedMediaTypes={props.acceptedMediaTypes}
          />
        )}
        <ScrollView
          ref={scrollRef}
          horizontal={true}
          showsHorizontalScrollIndicator={true}
          scrollEnabled={true}
          contentContainerStyle={contentContainerStyle}
          persistentScrollbar={true}
        >
          {typedMedia.map((m) => (
            <View style={styles.mediaItemContainer} key={m.id}>
              <RenderMediaItem
                mediaItem={m}
                key={m.id}
                canDelete={props.canEditMedia}
                deleteMediaItem={(mediaId) =>
                  handleDelete(mediaId, props.onMediaDeleted)
                }
              />
            </View>
          ))}
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.containerCropCircle}>
      {typedMedia.length > 0 && (
        <ScrollView
          ref={scrollRef}
          horizontal={true}
          showsHorizontalScrollIndicator={true}
          scrollEnabled={true}
          contentContainerStyle={styles.scrollContentCropCircle}
          style={styles.scrollViewCropCircle}
        >
          {typedMedia.map((m) => (
            <View style={styles.mediaItemContainer} key={m.id}>
              <RenderMediaItem
                mediaItem={m}
                key={m.id}
                canDelete={props.canEditMedia}
                deleteMediaItem={(mediaId) =>
                  handleDelete(mediaId, props.onMediaDeleted)
                }
              />
            </View>
          ))}
        </ScrollView>
      )}
      {canAddMedia && (
        <MediaPicker
          workspaceId={props.workspaceId}
          setMedia={(media) => handleAdd(media, props.onMediaAdded)}
          mode={pickerMode}
          icon={'plus'}
          iconColor={Colors.black}
          iconSize={24}
          setProgress={handleUploadProgress}
          setUploading={props.onUploadingChanged}
          style={styles.pickerCropCircle}
          acceptedMediaTypes={props.acceptedMediaTypes}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    display: 'flex',
    alignSelf: 'flex-start',
    justifyContent: 'space-between',
  },
  containerCropCircle: {
    flexDirection: 'row',
    display: 'flex',
  },
  scrollViewCropCircle: {
    flexGrow: 0,
    flexShrink: 1,
  },
  scrollContentCropCircle: {
    alignItems: 'center',
  },
  picker: {
    alignSelf: 'center',
    height: 80,
    width: 80,
    borderRadius: 8,
    marginLeft: 2,
    marginRight: 2,
    marginBottom: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primaryGrayBackground,
  },
  pickerCropCircle: {
    height: 88,
    width: 88,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    marginTop: 12,
  },
  mediaItemContainer: {
    marginLeft: 8,
    marginRight: 2,
  },
});
